import React, { useState } from "react";
import GuideSection from "../components/guide/GuideSection";
import { shareCapitalGuide } from "../components/guide/guideContent";
import { useNavigate } from "react-router-dom";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import Header from "../components/header/Header";
import ProgressBar from "../components/progressBar/ProgressBar";
import { FiArrowRight } from "react-icons/fi";
import FlowField from "../components/forms/FlowField";
import "./styles/flow-form.css";
import { addToCart } from "../redux/actions/CartActions";
import { useDispatch, useSelector } from "react-redux";
import Swal from "sweetalert2";
import Loader from "../components/loader/Loader";
import { updateBusinessRequest } from "../redux/actions/RequestActions";

// Définition des montants minimums et maximums par type d'entreprise
const CAPITAL_LIMITS = {
  ETS: { min: 0, max: 999999 },
  SARL: { min: 100000, max: 999999 },
  SARLU: { min: 100000, max: 5000000 },
  SAS: { min: 100000, max: 999999 },
  SASU: { min: 100000, max: 999999 },
  SNC: { min: 100000, max: 999999 },
  SCS: { min: 100000, max: 999999 },
  ASSOCIATION: { min: 100000, max: 999999 },
  SA: { min: 500000, max: 999999 },
};

function ShareCapital() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const currentStep = "Entreprise";
  const steps = [
    "Propriétaire",
    "Entreprise",
    "Services",
    "Paiement",
    "Finalisation",
  ];
  const cart = useSelector((state) => state.cart);
  const { user } = useSelector((state) => state.authUser);
  const { businessCreationRequest } = useSelector((state) => state.request);
  const [submitting, setSubmitting] = useState(false);

  // Récupération des limites selon le type d'entreprise
  const { min: minCapital, max: maxCapital } = CAPITAL_LIMITS[
    cart?.businessType
  ] || {
    min: 0,
    max: 999999,
  };

  // Validation schema
  const validationSchema = Yup.object().shape({
    shareCapital: Yup.number()
      .typeError("Veuillez entrer un nombre valide")
      .required("Le montant est requis")
      .min(
        minCapital,
        `Le capital minimum pour une ${
          cart?.businessType
        } est de ${minCapital.toLocaleString()} XAF`,
      )
      .max(
        maxCapital,
        `Le capital ne peut pas dépasser ${maxCapital.toLocaleString()} XAF`,
      ),
  });

  const handleSubmit = async (values) => {
    setSubmitting(true);
    await dispatch(
      addToCart({
        shareCapital: values.shareCapital,
      }),
    );

    await dispatch(
      updateBusinessRequest(
        user.token,
        businessCreationRequest.reference,
        {
          shareCapital: values.shareCapital,
        },
        handleCallback,
      ),
    );
  };

  const handleCallback = ({ success, message }) => {
    const Toast = Swal.mixin({
      toast: true,
      position: "top-end",
      showConfirmButton: false,
      timer: 4000,
    });
    if (success) {
      navigate("/business-name");
    } else {
      Toast.fire({
        icon: "error",
        title: "Échec",
        text: message,
      });
    }
    setSubmitting(false);
  };

  return (
    <div className="flow-form-page">
      <Header />
      <Loader isLoading={submitting} />
      <ProgressBar steps={steps} currentStep={currentStep} />

      <header className="flow-form-header">
        <h1 className="flow-form-title">
          Quel <span>capital social</span> souhaitez-vous déclarer ?
        </h1>
        <p className="flow-form-lead">
          C'est le montant inscrit dans les statuts de votre{" "}
          {cart?.businessType}. Vous n'avez rien à nous verser à ce titre.
        </p>
      </header>

      <Formik
        initialValues={{
          shareCapital: cart.shareCapital ?? "",
        }}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
      >
        <Form className="flow-form" noValidate>
          <FlowField
            name="shareCapital"
            type="number"
            inputMode="numeric"
            min={minCapital}
            label="Capital social (FCFA)"
            placeholder={`Ex. : ${minCapital.toLocaleString("fr-FR")}`}
            hint={`Entre ${minCapital.toLocaleString(
              "fr-FR",
            )} et ${maxCapital.toLocaleString("fr-FR")} FCFA pour une ${
              cart?.businessType
            }.`}
          />

          <div className="flow-actions">
            <button type="submit" className="flow-submit" disabled={submitting}>
              Continuer <FiArrowRight aria-hidden="true" />
            </button>
          </div>
        </Form>
      </Formik>

      <GuideSection {...shareCapitalGuide} />
    </div>
  );
}

export default ShareCapital;
